import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { brandInvitationsPath } from "@/routes"

interface InviteMemberDialogProps {
  /** Trigger element passed via `<DialogTrigger asChild>`. */
  trigger: ReactNode
  /**
   * Optional ref forwarded for focus return on close. Standard shadcn
   * flow handles the trigger-on-page case automatically; pass this only
   * when mounting via state-controlled `open` from elsewhere on the page
   * (e.g. an empty-state CTA outside the trigger's scope).
   */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * OKS-14 — Brand Admin invites a Brand Member (or Brand Admin — tier
 * decided server-side at invitation creation, defaults `member` per
 * PRD AC1).
 *
 * Single-field dialog. Posts to `brandInvitationsPath()` — no `path`
 * discriminator (single endpoint, single shape). Tier is implicit:
 * the Path A endpoint always lands invitees as `tenant_role: member`
 * on accept (Backend's service contract). No Role select per PRD §1.
 *
 * Component-sharing decision (port-from-communities): structurally
 * identical to OKS-13's `InviteTeammateDialog` modulo endpoint URL +
 * helper copy. Per the FE TRD Port Assessment, kept as a separate
 * component for v1 (2 consumers ≠ a primitive). Will promote to a
 * shared `InviteByEmailDialog` primitive when the 3rd consumer lands
 * (likely Crew Admin invite from OKS Admin in a future ticket).
 *
 * Crew form rules: Inertia `<Form>` (NOT shadcn `Form`); plain `Input`
 * + `Label` + `<InputError>`; no react-hook-form; no zod. Email auto
 * trim+lowercase on blur (parity with OKS-12 / OKS-13).
 *
 * **Non-dismissable while submitting** — `<Dialog onOpenChange>` blocks
 * ESC + overlay-click while `processing`. Tab order: Email → Cancel →
 * Submit.
 *
 * Locked QA testid (OKS-14 v4 freeze):
 *   - `brand-invite-member-trigger` on the trigger element (consumer Button)
 *   - `brand-invite-member-dialog` on the DialogContent
 *   - `brand-invite-member-submit` on the primary button
 *   - `brand-invite-member-error-banner` / `brand-invite-member-offline-banner`
 */
export function InviteMemberDialog({
  trigger,
  triggerRef,
}: InviteMemberDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  /**
   * Mid-submit guard for `onOpenChange`. Mirrors `processing` from the
   * Inertia `<Form>` render-prop on every render so ESC + overlay-click
   * can read the latest value outside the Form scope.
   */
  const submittingRef = useRef(false)

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value)
  }

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.target.value.trim().toLowerCase()
    if (next !== email) setEmail(next)
  }

  function handleClose() {
    setOpen(false)
    setEmail("")
    triggerRef?.current?.focus()
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else setOpen(true)
      }}
    >
      <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid="brand-invite-member-dialog"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {t("brand.team.invite_dialog.title")}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {t("brand.team.invite_dialog.description")}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/* OKS-120 — Form acts as scrollable body in mobile Sheet mode. */}
        <Form
          action={brandInvitationsPath()}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            return (
              <>
                {errors.base && (
                  <div
                    role="alert"
                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="brand-invite-member-error-banner"
                  >
                    {Array.isArray(errors.base)
                      ? errors.base.join(" ")
                      : errors.base}
                  </div>
                )}
                {!online && (
                  <div
                    className="border-muted-foreground/20 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="brand-invite-member-offline-banner"
                  >
                    {t("brand.team.invite_dialog.offline_banner")}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="brand-invite-member-email">
                    {t("brand.team.invite_dialog.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="brand-invite-member-email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    autoFocus
                    aria-required="true"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder={t(
                      "brand.team.invite_dialog.email_placeholder",
                    )}
                    aria-invalid={errors.email ? "true" : undefined}
                    aria-describedby={
                      errors.email
                        ? "brand-invite-member-email-error"
                        : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.email}
                    id="brand-invite-member-email-error"
                  />
                  {/*
                   * Helper text per UX Spec — clarifies what the invitee
                   * lands as. Static copy; the BE6 tier defaults to
                   * "member" on accept (PRD AC1).
                   */}
                  <p className="text-muted-foreground text-xs">
                    {t("brand.team.invite_dialog.helper")}
                  </p>
                </div>

                <ResponsiveDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {t("brand.team.invite_dialog.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={processing || !online}
                    aria-disabled={processing || !online ? "true" : undefined}
                    data-testid="brand-invite-member-submit"
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing
                      ? t("brand.team.invite_dialog.sending")
                      : t("brand.team.invite_dialog.submit")}
                  </Button>
                </ResponsiveDialogFooter>
              </>
            )
          }}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
